import { Card, Form, Nav, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppContext } from '@/Main';
import { Outlet, useLocation } from 'react-router-dom';
import { getColor } from '@/helpers/utils';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import Flex from '@/components/common/Flex';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { WizardLayoutProps } from '../types/purchaseTypes';

type NavItemConfig = {
  icon: IconProp;
  label: string;
  step: number;
  path: string;
};

type NavItemProps = {
  index: number;
  step?: number;
  icon: IconProp;
  label: string;
  path: string;
};

function WizardLayout({ variant, validation, progressBar }: WizardLayoutProps) {
  const { isRTL } = useAppContext();
  const pathname = useLocation().pathname;
  const navItems: NavItemConfig[] = [
    {
      icon: 'window-restore',
      label: 'ارتقا یا تغییر',
      step: 1,
      path: ''
    },
    {
      icon: 'redo-alt',
      label: 'خرید طرح',
      step: 2,
      path: '/prorogate-or-upgrade'
    },
    {
      icon: 'check-double',
      label: 'تایید سفارش',
      step: 3,
      path: '/prorogate-or-upgrade/order'
    },
    {
      icon: 'credit-card',
      step: 4,
      label: 'پرداخت',
      path: '/prorogate-or-upgrade/payment'
    }
  ];
  const [currentNavItem, setCurrentNavItem] = useState<NavItemConfig>(navItems[0]);
  const isPaymentPage = pathname.includes('/payment');
  useEffect(() => {
    setCurrentNavItem(pathToNavItem());
  }, [pathname]);
  const pathToNavItem = (): NavItemConfig => {
    const currentNavItem = [...navItems]
      ?.reverse()
      ?.find((item) => pathname?.includes(item.path));
    return currentNavItem ?? navItems[0];
  };
  return (
    <>
      <style>
        {`
      .theme-wizard .nav-link.active:not(.nav-pills .active){
        color: var(--falcon-navbar-vertical-default-link-active-color)
      }
      .theme-wizard .nav-link.active .nav-item-circle{
        background-color: var(--falcon-navbar-vertical-default-link-active-color) !important
      }
      `}
      </style>
      <Card as={Form} noValidate className="theme-wizard mb-5">
        {!isPaymentPage && (
          <>
            <Card.Header
              className={classNames('bg-body-tertiary', {
                'px-4 py-3': variant === 'pills',
                'pb-2': !variant
              })}
            >
              <Nav className="justify-content-center" variant={variant}>
                {variant === 'pills'
                  ? navItems.map((item, index) => (
                      <NavItemPill
                        key={item.label}
                        index={index + 1}
                        step={currentNavItem?.step}
                        icon={item.icon}
                        path={item.path}
                        label={item.label}
                      />
                    ))
                  : navItems.map((item, index) => (
                      <NavItem
                        key={item.label}
                        index={index + 1}
                        path={item.path}
                        step={currentNavItem?.step}
                        icon={item.icon}
                        label={item.label}
                      />
                    ))}
              </Nav>
            </Card.Header>
            {progressBar && (
              <ProgressBar now={(currentNavItem?.step ?? 0) * 25} style={{ height: 2 }} />
            )}
          </>
        )}
        <Card.Body className="fw-normal p-0">
          <Outlet />
        </Card.Body>
      </Card>
    </>
  );
};

const NavItem = ({ index, step = 0, icon, label, path }: NavItemProps) => {
  const isClickable = step > index;
  return isClickable ? (
    <Nav.Item>
      <Nav.Link
        to={step > index ? path : '#'}
        as={Link}
        className={classNames('fw-semi-bold', {
          done: index < 4 ? step > index : step > 3,
          active: step === index
        })}
      >
        <span className="nav-item-circle-parent p-0 p-md-1">
          <span className="nav-item-circle">
            <FontAwesomeIcon icon={icon} />
          </span>
        </span>
        <span className="d-none d-md-block mt-1 fs--1">{label}</span>
      </Nav.Link>
    </Nav.Item>
  ) : (
    <Nav.Item>
      <Nav.Link
        className={classNames('fw-semi-bold', {
          done: index < 4 ? step > index : step > 3,
          active: step === index
        })}
      >
        <span className="nav-item-circle-parent p-0 p-md-1">
          <span className="nav-item-circle">
            <FontAwesomeIcon icon={icon} />
          </span>
        </span>
        <span className="d-none d-md-block mt-1 fs--1">{label}</span>
      </Nav.Link>
    </Nav.Item>
  );
};

const NavItemPill = ({ index, step = 0, icon, label }: Omit<NavItemProps, 'path'>) => {
  return (
    <Nav.Item>
      <Nav.Link
        as={Link}
        className={classNames('fw-semi-bold', {
          done: step > index
        })}
        style={{
          backgroundColor: step === index && getColor('primary'),
          color: step === index && getColor('gray-100')
        }}
      >
        <Flex alignItems="center" justifyContent="center">
          <FontAwesomeIcon icon={icon} />
          <span className="d-none d-md-block mt-1 fs--1 ms-2">{label}</span>
        </Flex>
      </Nav.Link>
    </Nav.Item>
  );
};

export default WizardLayout;
